import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDynamicWidgets, useRefinementList, useStats } from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import {
  DEALS_FACET_ATTRIBUTE,
  mergeDealsFacetIntoAttributes,
  resolveStableDynamicWidgetAttributes,
} from '@/helpers/utils/dealsFacetUtils';
import { useProductListingContext } from './context/productListingContext';
import { CustomRefinementList } from './customRefinementList';

function getTopThreeKeysByIndex(mapData: any, slice: number) {
  const dataArray = Array.isArray(mapData) ? mapData : Array.from(mapData);
  const sortedArray = dataArray.sort((a, b) => a?.[1] - b?.[1]);
  return sortedArray?.slice(0, slice)?.map((arr) => arr?.[0]);
}

const getExpandedFacetList = (expandFacetList: any) => {
  return expandFacetList?.map((facet: any) => facet?.expanded_facet) || [];
};

const findMatchingPageData = (pages: any, currentPage: string) => {
  return pages?.find((page: any) => page?.link?.href?.trim() === currentPage);
};

export const CustomFacets = () => {
  const { facets, categoryHit, blpExcludedFacets, pageType, expandedFacetList, appliedFilters } =
    useProductListingContext();
  const { attributesToRender } = useDynamicWidgets();
  const lastStableAttributesRef = useRef<string[]>([]);
  const stableAttributesToRender = useMemo(() => {
    const { attributes, lastStableAttributes } = resolveStableDynamicWidgetAttributes(
      attributesToRender,
      lastStableAttributesRef.current,
    );
    lastStableAttributesRef.current = lastStableAttributes;
    return attributes;
  }, [attributesToRender]);
  let parsedFacets = {};
  try {
    if (typeof facets === 'string') {
      parsedFacets = JSON.parse(facets);
    }
  } catch (e) {
    console.error('Error parsing facet JSON');
    parsedFacets = facets;
  }
  const facetEntries: [string, string][] = parsedFacets ? Object.entries(parsedFacets) : [];
  const { items: categoryURLFilters } = useRefinementList({ attribute: 'categoryURLFilter', limit: 1000 });
  const projectSettings: ProjectSettings = useProjSettings();
  const blockFacet = projectSettings?.blockFacet || '';
  const blockFacetArray: any = blockFacet?.split(',').map((item) => item.trim());
  const [mappedFacets, setMappedFacets] = useState(new Map());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCategoryDataAvailable, setIsCategoryDataAvailable] = useState(false);
  const [defaultExpandedFacets, setDefaultExpandedFacets] = useState<any>([]);
  const [currentPageExpandedFacets, setCurrentPageExpandedFacets] = useState<any>([]);
  const { nbHits } = useStats();
  const isFacetExistsInExcludedList = (facet: string): boolean => {
    if (!blpExcludedFacets || blpExcludedFacets?.length === 0) {
      return false;
    }
    return blpExcludedFacets?.includes(facet);
  };

  useEffect(() => {
    const defExpandedFacets = getTopThreeKeysByIndex(mappedFacets, 3);
    setDefaultExpandedFacets(defExpandedFacets ?? []);
  }, [mappedFacets]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const excludedParams = ['page', 'sort', 'priceRange'];
    if (params?.size > 0) {
      excludedParams.forEach((param) => {
        if (params.has(param)) {
          params.delete(param);
        }
      });
    }
    let currentPage = `${window?.location?.pathname}?${params?.toString()}`;
    if (params?.size === 0) {
      currentPage = `${window?.location?.pathname}`;
    }
    const matchingPage = findMatchingPageData(expandedFacetList?.pageFacet, currentPage);
    let facets = [];
    const currentPageFacets =
      params
        ?.get('facet')
        ?.split('|')
        ?.map((facetName) => facetName?.split(':')?.[0]) || [];
    if (matchingPage) {
      facets = getExpandedFacetList(matchingPage?.expanded_facets_list);
    } else if (!matchingPage) {
      let currentPageWithoutFacet = '';
      if (pageType === 'search') {
        currentPageWithoutFacet = `${window?.location?.pathname}?q=${params?.get('q')}`;
      } else {
        currentPageWithoutFacet = window?.location?.pathname?.split('/_/')?.[0];
      }
      const matchingPageWithoutFacet = findMatchingPageData(expandedFacetList?.pageFacet, currentPageWithoutFacet);
      if (matchingPageWithoutFacet) {
        facets = getExpandedFacetList(matchingPageWithoutFacet?.expanded_facets_list);
      } else {
        facets = getExpandedFacetList(expandedFacetList?.globalFacet);
      }
    } else {
      facets = getExpandedFacetList(expandedFacetList?.globalFacet);
    }
    setCurrentPageExpandedFacets(new Set([...facets, ...currentPageFacets]));
  }, [appliedFilters, expandedFacetList]);

  function moveClearanceToEnd(arr: any) {
    // Filter out 'clearance' and create a new array without it
    const filteredArray = arr.filter((item: any) => item !== 'clearance');

    // Add 'clearance' to the end
    if (arr.includes('clearance')) {
      filteredArray.push('clearance');
    }

    return filteredArray;
  }

  const fallbackFacetAttributes = useMemo(
    () => moveClearanceToEnd(mergeDealsFacetIntoAttributes(facetEntries.map(([facetKey]) => facetKey))),
    [facetEntries],
  );
  const useJsonFallback = !stableAttributesToRender?.length;
  const dynamicAttributesToRender = useMemo(
    () => moveClearanceToEnd(mergeDealsFacetIntoAttributes(stableAttributesToRender)),
    [stableAttributesToRender],
  );

  return (
    <>
      {useJsonFallback
        ? fallbackFacetAttributes.map((facetKey: string, idx: number) => {
            const isPreSelectFacetKey = pageType === 'brands' && isFacetExistsInExcludedList(facetKey);
            const expandedFacetsList = [...currentPageExpandedFacets, ...defaultExpandedFacets];
            const isCollapsed = expandedFacetsList?.find((item: any) => item === facetKey) ? true : false;
            return (
              <React.Fragment key={facetKey}>
                {!isPreSelectFacetKey ? (
                  <div key={facetKey}>
                    <CustomRefinementList
                      attribute={facetKey}
                      facetEntries={facetEntries}
                      limit={
                        facetKey === 'colorRefinement'
                          ? 16
                          : facetKey === DEALS_FACET_ATTRIBUTE
                          ? 1000
                          : blockFacetArray?.includes(facetKey)
                          ? 12
                          : 8
                      }
                      showMore
                      showMoreLimit={1000}
                      isCollapsed={isCollapsed}
                      map={mappedFacets}
                      setMappedFacets={setMappedFacets}
                      idx={idx}
                      nbHits={nbHits}
                      categoryURLFilters={categoryURLFilters}
                      categoryHit={categoryHit}
                      setIsCategoryDataAvailable={setIsCategoryDataAvailable}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </React.Fragment>
            );
          })
        : dynamicAttributesToRender?.map((attribute: any, idx: number) => {
            const isPreSelectFacetKey = pageType === 'brands' && isFacetExistsInExcludedList(attribute);
            const expandedFacetsList = [...currentPageExpandedFacets, ...defaultExpandedFacets];
            const isCollapsed = expandedFacetsList?.find((item: any) => item === attribute) ? true : false;
            return (
              <React.Fragment key={attribute}>
                {!isPreSelectFacetKey ? (
                  <div key={attribute}>
                    <CustomRefinementList
                      attribute={attribute}
                      facetEntries={facetEntries}
                      limit={
                        attribute === 'colorRefinement'
                          ? 16
                          : attribute === DEALS_FACET_ATTRIBUTE
                          ? 1000
                          : blockFacetArray?.includes(attribute)
                          ? 12
                          : 8
                      }
                      showMore
                      showMoreLimit={1000}
                      isCollapsed={isCollapsed}
                      map={mappedFacets}
                      setMappedFacets={setMappedFacets}
                      idx={idx}
                      nbHits={nbHits}
                      categoryURLFilters={categoryURLFilters}
                      categoryHit={categoryHit}
                      setIsCategoryDataAvailable={setIsCategoryDataAvailable}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </React.Fragment>
            );
          })}
    </>
  );
};
